import * as React from 'react';
import {
  Box,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Input,
  Pagination,
  Popover,
  Table,
} from '@amzn/awsui-components-react-v3';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { EmptyState } from 'src/commons/EmptyState';
import {
  Column,
  EditDataSetRequest,
  GetDataSetResponseStructure,
} from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { useEffect, useState } from 'react';
import { defaultWrapLinesPreference, mediumPageSizePreference, paginationLabels } from 'src/commons/tables';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { editDataset } from 'src/api/catalog';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface ColumnsTableProps extends CommonPageProps {
  version: any;
  dataset?: GetDataSetResponseStructure;
  isPartitionKeysTable?: boolean;
  notifyEditFailure?: any;
  handleFetchDataset?: any;
}

export const ColumnsTable = (props: ColumnsTableProps) => {
  const [tableColumns, setTableColumns] = useState<Column[]>([]);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 20,
    wrapLines: true,
  });

  function handleRefresh(businessDescriptionColumns: Column[]) {
    const sourceColumns = props.isPartitionKeysTable
      ? props.version?.PartitionKeys || []
      : props.version?.Columns || [];
    const columns = sourceColumns.map((col: Column) => {
      const bizDesc = businessDescriptionColumns?.find((c: any) => c.Name === col.Name);
      return bizDesc && bizDesc.Comment ? { ...col, Comment: bizDesc.Comment } : col;
    });
    setTableColumns(columns);
  }

  useEffect(() => {
    handleRefresh(props.dataset?.SchemaBusinessDescription?.Columns);
  }, [props.version, props.dataset]);

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (item) => item.Name,
      width: 280,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item) => item.Type,
      width: 160,
    },
    {
      id: 'description',
      header: (
        <>
          <Popover
            dismissButton={false}
            position='top'
            size='medium'
            triggerType='text'
            content={
              <Box variant='p'>
                Business description for the column. Overrides description from source if present. <br />
                Clear the custom description to view the source description.
              </Box>
            }
          >
            Description
          </Popover>
        </>
      ),
      cell: (item) => item.Comment,
      minWidth: 250,
      editConfig: {
        ariaLabel: 'Description',
        editIconAriaLabel: 'editable',
        errorIconAriaLabel: 'Description edit error',
        editingCell: (item, { currentValue, setValue }) => {
          return (
            <Input
              autoFocus={true}
              value={currentValue ?? item.Comment}
              onChange={(event) => {
                setValue(event.detail.value);
              }}
            />
          );
        },
      },
    },
  ];

  const handleSubmitEdit = async (item: Column, column: TableProps.ColumnDefinition<Column>, newValue: string) => {
    const updatedColumns = tableColumns.map((c) => {
      if (c.Name === item.Name) {
        return { ...c, Comment: newValue };
      }
      return c;
    });
    // Update only required columns.
    const updatedColumn = updatedColumns.find((c) => c.Name === item.Name);
    if (updatedColumn) {
      try {
        let request: EditDataSetRequest = {
          Id: props.dataset?.Id,
          SchemaBusinessDescription: {
            Columns: [updatedColumn],
          },
        };
        await editDataset(request);
        handleRefresh(updatedColumns);
        if (props.handleFetchDataset) {
          props.handleFetchDataset();
        }
      } catch (err) {
        console.error('Failed to update dataset with business descriptions..', err);
        if (props.notifyEditFailure) {
          props.notifyEditFailure('Unable to edit column descriptions! Cause: ' + err.message);
        }
      }
    }
  };

  const { items, collectionProps, paginationProps } = useCollection(tableColumns, {
    pagination: { pageSize: preferences.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0], isDescending: false } },
  });

  return (
    <Table
      {...collectionProps}
      loadingText='Loading columns..'
      columnDefinitions={columnDefinitions}
      items={items}
      resizableColumns
      wrapLines={preferences.wrapLines}
      header={
        <Header
          counter={
            tableColumns.length
              ? items.length == tableColumns.length
                ? `(${tableColumns.length})`
                : `(${items.length} of ${tableColumns.length})`
              : '(0)'
          }
        >
          {props.isPartitionKeysTable ? 'Partition keys' : 'Columns'}
        </Header>
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      empty={
        <EmptyState
          title={props.isPartitionKeysTable ? 'No partition keys' : 'No columns'}
          subtitle={props.isPartitionKeysTable ? 'No partition keys found for this dataset.' : 'No columns to display.'}
        />
      }
      preferences={
        <CollectionPreferences
          title='Preferences'
          confirmLabel='Confirm'
          cancelLabel='Cancel'
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={mediumPageSizePreference}
          wrapLinesPreference={defaultWrapLinesPreference}
        />
      }
      submitEdit={handleSubmitEdit}
    />
  );
};
